/**
 * FeatureUpgradeModal (F-178 / US-137 / T-137-001 + T-137-002).
 *
 * Generic plan-gate upgrade modal driven by per-feature configuration:
 *  - T-137-001: feature value proposition (title, icon, bullet-point benefits).
 *  - T-137-002: ROI calculator seeded from the account's incident history
 *    (live monthly / annual USD avoided + staff hours recovered), plus an
 *    optional compliance/mandate warning (T-139-002, MTD) and a meeting CTA.
 *
 * One component serves every tier-gated feature (Evidence Safe, Smart Document
 * Reader, MTD, ...) — replacing pasted per-feature modal copies.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MeetingLinkCta from './MeetingLinkCta';

export interface FeatureRoiConfig {
    /** Cost of one avoided incident/penalty, in USD (documented basis). */
    incidentCostUsd: number;
    /** Staff hours recovered per incident. */
    hoursPerIncident: number;
    /** Label for the ROI input (account-specific seeding context). */
    incidentsLabel: string;
    /** Basement note under the ROI metrics. */
    basisNote: string;
}

export interface FeatureUpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Human feature name, e.g. "Smart Document Reader". */
    featureName: string;
    /** FontAwesome icon class, e.g. "fas fa-file-lines". */
    icon: string;
    /** Required tier id (matches /pricing?plan=). */
    tier: string;
    /** Display label for the required tier, e.g. "Enterprise". */
    tierLabel: string;
    /** Feature value proposition bullets (T-137-001). */
    valueProps: string[];
    /** Optional ROI calculator config (T-137-002). */
    roi?: FeatureRoiConfig;
    /** Account's historical incidents per month (seeds the ROI input). */
    incidentHistory?: number;
    /** CTA href — defaults to /pricing?plan=<tier>&upgrade=1. */
    ctaHref?: string;
    /** Meeting-link campaign id (F-188 surface). */
    campaign: string;
    /** Optional compliance/mandate warning line (T-139-002). */
    complianceNote?: string;
    /** Optional gate reason to display under the title. */
    reason?: string;
    /** Modal testid override (defaults to feature-upgrade-modal). */
    testid?: string;
}

export interface FeatureRoi {
    incidentsPerMonth: number;
    monthlyAvoidedUsd: number;
    annualAvoidedUsd: number;
    annualHoursRecovered: number;
}

/** Pure ROI projection (T-137-002) — exported for tests. Clamps negatives to zero. */
export function calculateFeatureRoi(
    incidentsPerMonth: number,
    costPerIncident: number,
    hoursPerIncident: number
): FeatureRoi {
    const incidents = Number.isFinite(incidentsPerMonth) && incidentsPerMonth > 0 ? incidentsPerMonth : 0;
    const cost = Number.isFinite(costPerIncident) && costPerIncident > 0 ? costPerIncident : 0;
    const hours = Number.isFinite(hoursPerIncident) && hoursPerIncident > 0 ? hoursPerIncident : 0;
    const monthlyAvoidedUsd = incidents * cost;
    return {
        incidentsPerMonth: incidents,
        monthlyAvoidedUsd,
        annualAvoidedUsd: monthlyAvoidedUsd * 12,
        annualHoursRecovered: incidents * hours * 12,
    };
}

export function featureUpgradeCtaHref(tier: string): string {
    return `/pricing?plan=${encodeURIComponent(tier)}&upgrade=1`;
}

const money = (v: number) =>
    v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export const FeatureUpgradeModal: React.FC<FeatureUpgradeModalProps> = ({
    isOpen,
    onClose,
    featureName,
    icon,
    tier,
    tierLabel,
    valueProps,
    roi,
    incidentHistory = 2,
    ctaHref,
    campaign,
    complianceNote,
    reason,
    testid = 'feature-upgrade-modal',
}) => {
    const navigate = useNavigate();
    const [incidents, setIncidents] = useState<number>(incidentHistory);

    if (!isOpen) return null;

    const href = ctaHref || featureUpgradeCtaHref(tier);
    const roiResult = roi ? calculateFeatureRoi(incidents, roi.incidentCostUsd, roi.hoursPerIncident) : null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            data-testid={testid}
            role="dialog"
            aria-modal="true"
            aria-label={`${tierLabel} upgrade required`}
        >
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 md:p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <i className={`${icon} text-indigo-600`}></i>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">
                                {featureName} is a {tierLabel} feature
                            </h2>
                            {reason && (
                                <p className="text-xs text-gray-500" data-testid="gate-reason">{reason}</p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close upgrade dialog"
                        className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                    >
                        &times;
                    </button>
                </div>

                {/* Feature value proposition (T-137-001) */}
                <ul className="space-y-2 text-sm text-gray-700 mb-5" data-testid="feature-upgrade-value-prop">
                    {valueProps.map((vp) => (
                        <li key={vp} className="flex items-start">
                            <i className="fas fa-check text-emerald-500 mr-2 mt-0.5"></i>
                            {vp}
                        </li>
                    ))}
                </ul>

                {/* Compliance / mandate warning (T-139-002) */}
                {complianceNote && (
                    <div
                        className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-sm text-amber-900"
                        data-testid="compliance-warning"
                        role="alert"
                    >
                        <i className="fas fa-triangle-exclamation mr-2 text-amber-600"></i>
                        {complianceNote}
                    </div>
                )}

                {/* ROI calculator (T-137-002) */}
                {roi && roiResult && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
                        <h3 className="text-sm font-bold text-gray-900 mb-1">ROI calculator</h3>
                        <label htmlFor="feature-roi-incidents" className="block text-xs text-gray-600 mb-2">
                            {roi.incidentsLabel}
                        </label>
                        <input
                            id="feature-roi-incidents"
                            type="number"
                            min={0}
                            max={999}
                            value={incidents}
                            data-testid="roi-incidents-input"
                            onChange={(e) => setIncidents(Math.max(0, parseInt(e.target.value || '0', 10) || 0))}
                            className="w-24 border border-gray-300 rounded-lg px-3 py-1.5 text-sm mb-3"
                        />
                        <dl className="grid grid-cols-3 gap-2 text-center">
                            <div>
                                <dt className="text-[11px] text-gray-500">Avoided / month</dt>
                                <dd className="text-sm font-bold text-gray-900" data-testid="roi-monthly">
                                    {money(roiResult.monthlyAvoidedUsd)}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-[11px] text-gray-500">Avoided / year</dt>
                                <dd className="text-sm font-bold text-gray-900" data-testid="roi-annual">
                                    {money(roiResult.annualAvoidedUsd)}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-[11px] text-gray-500">Hours recovered / year</dt>
                                <dd className="text-sm font-bold text-gray-900" data-testid="roi-hours">
                                    {roiResult.annualHoursRecovered}
                                </dd>
                            </div>
                        </dl>
                        <p className="text-[11px] text-gray-400 mt-2">{roi.basisNote}</p>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                    <a
                        href={href}
                        data-testid="feature-upgrade-cta"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate(href);
                            onClose();
                        }}
                        className="flex-1 bg-indigo-600 text-white text-center px-5 py-2.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                    >
                        Upgrade to {tierLabel}
                    </a>
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 rounded-lg font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 transition"
                    >
                        Not now
                    </button>
                </div>

                <div className="text-center mt-4">
                    <MeetingLinkCta
                        tier={tier}
                        campaign={campaign}
                        label="Or book a 1:1 with our team"
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                    />
                </div>
            </div>
        </div>
    );
};

export default FeatureUpgradeModal;